import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ChevronRight, Building2, Users, Layers, Palette,
  UserCheck, UserX, Plus, KeyRound, Pencil, X,
  Loader2, Check, AlertTriangle, Upload, Save, Image,
  CheckCircle2, FileText, FlaskConical, Scan, Receipt,
} from 'lucide-react';
import { clsx } from 'clsx';
import { adminApi, planApi, adminBrandingApi } from '@/lib/api';
import type { Branding } from '@/hooks/useBranding';

// ── Constants ──────────────────────────────────────────────────────
const PLAN_LABELS: Record<string, string> = {
  base:  'Base',
  plus:  'Plus',
  pro:   'Pro',
};
const STATUS_STYLES: Record<string, string> = {
  active:       'bg-clinical-success/10 text-clinical-success',
  pending:      'bg-orange/10 text-orange-dark',
  under_review: 'bg-blue-50 text-clinical-info',
  suspended:    'bg-red-50 text-clinical-danger',
  cancelled:    'bg-gray-100 text-gray-400',
  retired:      'bg-gray-100 text-gray-400',
};

const STATUS_OPTIONS = [
  { value: 'active',       label: 'Active'       },
  { value: 'suspended',    label: 'Suspended'    },
  { value: 'under_review', label: 'Under Review' },
  { value: 'cancelled',    label: 'Cancelled'    },
  { value: 'retired',      label: 'Retired (soft delete)' },
];

function paise(p: number) {
  return `₹${(p / 100).toLocaleString('en-IN')}`;
}

const ROLE_COLORS: Record<string, string> = {
  hospital_admin:  'bg-navy/10 text-navy',
  doctor:          'bg-blue-50 text-clinical-info',
  nurse:           'bg-purple-50 text-purple-600',
  receptionist:    'bg-orange/10 text-orange-dark',
  lab_technician:  'bg-yellow-50 text-yellow-700',
  pharmacist:      'bg-green-50 text-clinical-success',
  billing_staff:   'bg-pink-50 text-pink-600',
  accountant:      'bg-indigo-50 text-indigo-600',
  radiologist:     'bg-teal-50 text-teal-600',
};

const ROLES = [
  { value: 'doctor',         label: 'Doctor' },
  { value: 'nurse',          label: 'Nurse' },
  { value: 'receptionist',   label: 'Receptionist' },
  { value: 'lab_technician', label: 'Lab Technician' },
  { value: 'pharmacist',     label: 'Pharmacist' },
  { value: 'billing_staff',  label: 'Billing Staff' },
  { value: 'accountant',     label: 'Accountant' },
  { value: 'radiologist',    label: 'Radiologist' },
  { value: 'hospital_admin', label: 'Hospital Admin' },
];

const MODULE_GROUPS = ['Clinical', 'Billing', 'Integrations', 'Comms', 'Platform'];

// ── Schemas ────────────────────────────────────────────────────────
const profileSchema = z.object({
  hospital_name:      z.string().min(2),
  contact_email:      z.string().email(),
  contact_mobile:     z.string().regex(/^\d{10}$/, '10 digits'),
  city:               z.string().min(1),
  state:              z.string().min(1),
  pin_code:           z.string().length(6, '6 digits'),
  bed_count_category: z.string().min(1),
  specialty_type:     z.string().min(1),
});

const userSchema = z.object({
  name:           z.string().min(2),
  email:          z.string().email(),
  mobile:         z.string().regex(/^\d{10}$/, '10 digits'),
  role:           z.string().min(1),
  specialization: z.string().optional(),
  password:       z.string().min(8),
});

type ProfileForm = z.infer<typeof profileSchema>;
type UserForm    = z.infer<typeof userSchema>;

// ── Field/Label helpers ────────────────────────────────────────────
const fld = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';
const lbl = 'block text-xs font-medium text-gray-500 mb-1';

// ── Overview Tab ───────────────────────────────────────────────────
function OverviewTab({ hospital, id }: { hospital: any; id: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      hospital_name:      hospital.hospital_name,
      contact_email:      hospital.contact_email,
      contact_mobile:     hospital.contact_mobile,
      city:               hospital.city,
      state:              hospital.state,
      pin_code:           hospital.pin_code,
      bed_count_category: hospital.bed_count_category,
      specialty_type:     hospital.specialty_type,
    },
  });

  const updateMut = useMutation({
    mutationFn: (data: ProfileForm) => adminApi.updateHospital(id, data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['hospital', id] }); setEditing(false); },
  });

  const statusMut = useMutation({
    mutationFn: (status: string) => adminApi.setStatus(id, status),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['hospital', id] }); qc.invalidateQueries({ queryKey: ['admin-hospitals'] }); setStatusConfirm(''); },
  });

  const planMut = useMutation({
    mutationFn: (plan: string) => adminApi.updateLimits(id, { plan }),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['hospital', id] }),
  });

  const { data: plansData } = useQuery({
    queryKey: ['plans'],
    queryFn:  () => planApi.list().then(r => r.data.plans as Array<{ slug: string; name: string; price_monthly: number; is_active: boolean }>),
  });
  const plansBySlug = Object.fromEntries((plansData ?? []).map(p => [p.slug, p]));
  const activePlans = (plansData ?? []).filter(p => p.is_active);

  const currentPlanName  = plansBySlug[hospital.plan]?.name  ?? hospital.plan;
  const currentPlanPrice = plansBySlug[hospital.plan] ? paise(plansBySlug[hospital.plan].price_monthly) : '';

  return (
    <div className="space-y-6">
      {/* Status + Plan row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Status</div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={clsx('inline-flex px-2.5 py-1 rounded-full text-xs font-medium',
              STATUS_STYLES[hospital.status] ?? 'bg-gray-100 text-gray-400')}>
              {hospital.status?.replace('_', ' ')}
            </span>
            <select
              value={statusConfirm}
              onChange={e => setStatusConfirm(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange/30">
              <option value="">Change status…</option>
              {STATUS_OPTIONS.filter(s => s.value !== hospital.status).map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {statusConfirm && (
              <button
                onClick={() => statusMut.mutate(statusConfirm)}
                disabled={statusMut.isPending}
                className="text-xs px-3 py-1 bg-clinical-danger text-white rounded-lg hover:opacity-90 flex items-center gap-1">
                {statusMut.isPending ? <Loader2 size={11} className="animate-spin"/> : <AlertTriangle size={11}/>}
                Confirm
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Subscription Plan</div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-bold text-navy">{currentPlanName}</span>
            <span className="text-xs text-gray-400">{currentPlanPrice}/mo</span>
            <select
              defaultValue={hospital.plan}
              onChange={e => planMut.mutate(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange/30">
              {activePlans.map(p => (
                <option key={p.slug} value={p.slug}>{p.name} — {paise(p.price_monthly)}</option>
              ))}
            </select>
            {planMut.isPending && <Loader2 size={13} className="animate-spin text-orange"/>}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Active Users', value: hospital.user_count ?? '—' },
          { label: 'Max Users',    value: hospital.max_users ?? '—' },
          { label: 'MRR',          value: currentPlanPrice || '—'   },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-navy">{value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <span className="text-sm font-semibold text-navy">Hospital Profile</span>
          {!editing ? (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-xs text-orange hover:text-orange-dark font-medium">
              <Pencil size={12}/> Edit
            </button>
          ) : (
            <button onClick={() => { setEditing(false); reset(); }}
              className="text-gray-400 hover:text-navy">
              <X size={16}/>
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleSubmit(d => updateMut.mutate(d))} className="p-5 grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={lbl}>Hospital Name *</label>
              <input {...register('hospital_name')} className={fld}/>
              {errors.hospital_name && <p className="text-clinical-danger text-xs mt-1">{errors.hospital_name.message}</p>}
            </div>
            <div>
              <label className={lbl}>Contact Email *</label>
              <input {...register('contact_email')} className={fld}/>
              {errors.contact_email && <p className="text-clinical-danger text-xs mt-1">{errors.contact_email.message}</p>}
            </div>
            <div>
              <label className={lbl}>Mobile *</label>
              <input {...register('contact_mobile')} className={fld}/>
              {errors.contact_mobile && <p className="text-clinical-danger text-xs mt-1">{errors.contact_mobile.message}</p>}
            </div>
            <div>
              <label className={lbl}>City *</label>
              <input {...register('city')} className={fld}/>
            </div>
            <div>
              <label className={lbl}>State *</label>
              <input {...register('state')} className={fld}/>
            </div>
            <div>
              <label className={lbl}>PIN Code *</label>
              <input {...register('pin_code')} className={fld}/>
            </div>
            <div>
              <label className={lbl}>Bed Category *</label>
              <input {...register('bed_count_category')} className={fld}/>
            </div>
            <div className="col-span-2">
              <label className={lbl}>Specialty Type *</label>
              <input {...register('specialty_type')} className={fld}/>
            </div>
            <div className="col-span-2 flex gap-3 pt-1">
              <button type="button" onClick={() => { setEditing(false); reset(); }}
                className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-navy hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={updateMut.isPending}
                className="flex-1 py-2 rounded-lg bg-orange text-navy font-semibold text-sm
                           hover:bg-orange-dark disabled:opacity-50 flex items-center justify-center gap-2">
                {updateMut.isPending && <Loader2 size={14} className="animate-spin"/>}
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {[
              ['Hospital Name',  hospital.hospital_name],
              ['Hospital ID',    hospital.schema_name],
              ['Contact Email',  hospital.contact_email],
              ['Mobile',         hospital.contact_mobile],
              ['City',           hospital.city],
              ['State',          hospital.state],
              ['PIN Code',       hospital.pin_code],
              ['Bed Category',   hospital.bed_count_category],
              ['Specialty',      hospital.specialty_type],
              ['Onboarding',     `Step ${hospital.onboarding_step}/5`],
              ['Created',        hospital.created_at ? new Date(hospital.created_at).toLocaleDateString('en-IN') : '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <div className="text-xs text-gray-400">{label}</div>
                <div className="font-medium text-navy mt-0.5">{value ?? '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modules Tab ────────────────────────────────────────────────────
function ModulesTab({ id }: { id: string }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['hospital-modules', id],
    queryFn:  () => adminApi.modules(id).then(r => r.data),
  });

  const toggleMut = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      adminApi.toggleModule(id, key, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hospital-modules', id] }),
  });

  const limitsMut = useMutation({
    mutationFn: (body: object) => adminApi.updateLimits(id, body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['hospital', id] }),
  });

  if (isLoading) return <div className="p-8 text-center text-gray-400 text-sm">Loading modules…</div>;

  const byGroup = MODULE_GROUPS.reduce<Record<string, any[]>>((acc, g) => {
    acc[g] = (data?.modules ?? []).filter((m: any) => m.group === g);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {/* Limits */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="text-sm font-semibold text-navy mb-4">Plan Limits</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Max Users</label>
            <div className="flex gap-2">
              <input
                type="number"
                defaultValue={data?.max_users}
                key={data?.max_users}
                id="max_users_input"
                className={clsx(fld, 'flex-1')}
                min={1} max={999}
              />
              <button
                onClick={() => {
                  const val = (document.getElementById('max_users_input') as HTMLInputElement)?.value;
                  limitsMut.mutate({ max_users: Number(val) });
                }}
                className="px-3 py-2 rounded-lg bg-navy text-white text-xs font-medium hover:bg-navy/80">
                Save
              </button>
            </div>
          </div>
          <div>
            <label className={lbl}>Storage (GB)</label>
            <div className="flex gap-2">
              <input
                type="number"
                defaultValue={data?.storage_gb}
                key={data?.storage_gb}
                id="storage_gb_input"
                className={clsx(fld, 'flex-1')}
                min={1} max={500}
              />
              <button
                onClick={() => {
                  const val = (document.getElementById('storage_gb_input') as HTMLInputElement)?.value;
                  limitsMut.mutate({ storage_gb: Number(val) });
                }}
                className="px-3 py-2 rounded-lg bg-navy text-white text-xs font-medium hover:bg-navy/80">
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Module toggles */}
      {MODULE_GROUPS.filter(g => byGroup[g]?.length > 0).map(group => (
        <div key={group} className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">{group}</div>
          <div className="space-y-1">
            {byGroup[group].map((mod: any) => (
              <div key={mod.key} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm text-navy">{mod.label}</span>
                <button
                  onClick={() => toggleMut.mutate({ key: mod.key, enabled: !mod.enabled })}
                  disabled={toggleMut.isPending}
                  className={clsx(
                    'relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0',
                    mod.enabled ? 'bg-clinical-success' : 'bg-gray-200'
                  )}>
                  <div className={clsx(
                    'absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200',
                    mod.enabled ? 'left-6' : 'left-1'
                  )}/>
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Users Tab ──────────────────────────────────────────────────────
function UsersTab({ id }: { id: string }) {
  const qc = useQueryClient();
  const [showAdd,   setShowAdd]   = useState(false);
  const [resetFor,  setResetFor]  = useState<string | null>(null);
  const [newPass,   setNewPass]   = useState('');
  const [apiErr,    setApiErr]    = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['hospital-users', id],
    queryFn:  () => adminApi.users(id).then(r => r.data),
  });

  const users: any[] = data?.users ?? [];

  const addMut = useMutation({
    mutationFn: (body: object) => adminApi.addUser(id, body),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['hospital-users', id] }); setShowAdd(false); addForm.reset({ role: 'doctor' }); setApiErr(null); },
    onError:    (e: any) => setApiErr(e.response?.data?.error ?? 'Failed to add user'),
  });

  const statusMut = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      adminApi.setUserStatus(id, userId, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hospital-users', id] }),
  });

  const resetMut = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      adminApi.resetUserPassword(id, userId, password),
    onSuccess: () => { setResetFor(null); setNewPass(''); },
    onError:   (e: any) => setApiErr(e.response?.data?.error ?? 'Reset failed'),
  });

  const addForm = useForm<UserForm>({
    resolver: zodResolver(userSchema),
    defaultValues: { role: 'doctor' },
  });

  const watchRole = addForm.watch('role');

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => { setShowAdd(true); setApiErr(null); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange text-navy font-semibold text-sm hover:bg-orange-dark">
          <Plus size={14}/> Add User
        </button>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">No users found for this hospital</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'Email', 'Role', 'Status', 'Last Login', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u: any) => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-navy">{u.name}</div>
                    {u.specialization && <div className="text-xs text-gray-400">{u.specialization}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize',
                      ROLE_COLORS[u.role] ?? 'bg-gray-100 text-gray-500')}>
                      {u.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx('inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                      u.is_active ? 'bg-clinical-success/10 text-clinical-success' : 'bg-gray-100 text-gray-400')}>
                      {u.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-IN') : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => statusMut.mutate({ userId: u.id, isActive: !u.is_active })}
                        title={u.is_active ? 'Suspend' : 'Activate'}
                        className="p-1.5 rounded text-gray-400 hover:text-navy hover:bg-gray-100 transition-colors">
                        {u.is_active ? <UserX size={14}/> : <UserCheck size={14}/>}
                      </button>
                      <button
                        onClick={() => { setResetFor(u.id); setNewPass(''); setApiErr(null); }}
                        title="Reset Password"
                        className="p-1.5 rounded text-gray-400 hover:text-orange hover:bg-orange/10 transition-colors">
                        <KeyRound size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add User modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="font-bold text-navy">Add Staff Member</h3>
              <button onClick={() => { setShowAdd(false); addForm.reset({ role: 'doctor' }); setApiErr(null); }}
                className="text-gray-400 hover:text-navy"><X size={18}/></button>
            </div>
            <form onSubmit={addForm.handleSubmit(d => addMut.mutate(d))} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className={lbl}>Full Name *</label>
                  <input {...addForm.register('name')} className={fld}/>
                  {addForm.formState.errors.name && <p className="text-clinical-danger text-xs mt-1">{addForm.formState.errors.name.message}</p>}
                </div>
                <div>
                  <label className={lbl}>Email *</label>
                  <input {...addForm.register('email')} type="email" className={fld}/>
                  {addForm.formState.errors.email && <p className="text-clinical-danger text-xs mt-1">{addForm.formState.errors.email.message}</p>}
                </div>
                <div>
                  <label className={lbl}>Mobile *</label>
                  <input {...addForm.register('mobile')} maxLength={10} className={fld}/>
                  {addForm.formState.errors.mobile && <p className="text-clinical-danger text-xs mt-1">{addForm.formState.errors.mobile.message}</p>}
                </div>
                <div>
                  <label className={lbl}>Role *</label>
                  <select {...addForm.register('role')} className={fld}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {watchRole === 'doctor' && (
                  <div>
                    <label className={lbl}>Specialization</label>
                    <input {...addForm.register('specialization')} className={fld}/>
                  </div>
                )}
                <div className="col-span-2">
                  <label className={lbl}>Temporary Password *</label>
                  <input {...addForm.register('password')} type="password" className={fld}/>
                  {addForm.formState.errors.password && <p className="text-clinical-danger text-xs mt-1">{addForm.formState.errors.password.message}</p>}
                </div>
              </div>
              {apiErr && <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-3 py-2">{apiErr}</div>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => { setShowAdd(false); addForm.reset({ role: 'doctor' }); setApiErr(null); }}
                  className="flex-1 py-2 rounded-lg border border-gray-200 text-navy text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={addMut.isPending}
                  className="flex-1 py-2 rounded-lg bg-orange text-navy font-semibold text-sm hover:bg-orange-dark
                             disabled:opacity-50 flex items-center justify-center gap-2">
                  {addMut.isPending && <Loader2 size={13} className="animate-spin"/>}
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password modal */}
      {resetFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-navy">Reset Password</h3>
              <button onClick={() => setResetFor(null)} className="text-gray-400 hover:text-navy"><X size={18}/></button>
            </div>
            <div>
              <label className={lbl}>New Password (min 8 chars)</label>
              <input
                type="password"
                value={newPass}
                onChange={e => setNewPass(e.target.value)}
                className={fld}
                placeholder="Min. 8 characters"
              />
            </div>
            {apiErr && <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-3 py-2">{apiErr}</div>}
            <div className="flex gap-3">
              <button onClick={() => setResetFor(null)}
                className="flex-1 py-2 rounded-lg border border-gray-200 text-navy text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => resetMut.mutate({ userId: resetFor, password: newPass })}
                disabled={newPass.length < 8 || resetMut.isPending}
                className="flex-1 py-2 rounded-lg bg-clinical-danger text-white font-semibold text-sm
                           hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2">
                {resetMut.isPending && <Loader2 size={13} className="animate-spin"/>}
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Branding Tab (platform admin) ─────────────────────────────────
function AdminField({
  label, hint, value, onChange, rows,
}: { label: string; hint?: string; value: string; onChange: (v: string) => void; rows?: number }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
      {rows ? (
        <textarea rows={rows} value={value} onChange={e => onChange(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy
                     focus:outline-none focus:ring-2 focus:ring-navy/20 resize-y"/>
      ) : (
        <input value={value} onChange={e => onChange(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-navy
                     focus:outline-none focus:ring-2 focus:ring-navy/20"/>
      )}
    </div>
  );
}

function AdminSectionCard({
  icon: Icon, title, children, onSave, saving, saved,
}: { icon: React.ElementType; title: string; children: React.ReactNode;
    onSave: () => void; saving: boolean; saved: boolean }) {
  return (
    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-navy"/>
          <span className="font-semibold text-navy text-sm">{title}</span>
        </div>
        <button onClick={onSave} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-orange text-gray-900
                     text-xs font-semibold hover:bg-orange-dark disabled:opacity-50 transition">
          {saving ? <Loader2 size={11} className="animate-spin"/> :
           saved   ? <CheckCircle2 size={11} className="text-green-700"/> : <Save size={11}/>}
          {saved ? 'Saved' : 'Save'}
        </button>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function BrandingTab({ hospitalId }: { hospitalId: string }) {
  const queryClient = useQueryClient();

  const { data: branding, isLoading } = useQuery<Branding>({
    queryKey: ['admin-branding', hospitalId],
    queryFn:  () => adminBrandingApi.get(hospitalId).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  const [identity, setIdentity] = useState({ branding_tagline: '', branding_license_no: '',
    hospital_address: '', hospital_phone: '', hospital_email: '' });
  const [rx,        setRx]        = useState({ branding_rx_footer: '' });
  const [invoice,   setInvoice]   = useState({ branding_invoice_footer: '' });
  const [lab,       setLab]       = useState({ branding_lab_footer: '' });
  const [radiology, setRadiology] = useState({ branding_radiology_footer: '' });
  const [logo,      setLogo]      = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [logoErr,   setLogoErr]   = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!branding) return;
    setIdentity({ branding_tagline: branding.branding_tagline ?? '',
      branding_license_no: branding.branding_license_no ?? '',
      hospital_address: branding.hospital_address ?? '',
      hospital_phone: branding.hospital_phone ?? '',
      hospital_email: branding.hospital_email ?? '' });
    setRx({ branding_rx_footer: branding.branding_rx_footer ?? '' });
    setInvoice({ branding_invoice_footer: branding.branding_invoice_footer ?? '' });
    setLab({ branding_lab_footer: branding.branding_lab_footer ?? '' });
    setRadiology({ branding_radiology_footer: branding.branding_radiology_footer ?? '' });
    setLogo(branding.branding_logo ?? '');
    setLogoPreview(branding.branding_logo ?? '');
  }, [branding]);

  const [saving,   setSaving]   = useState<Record<string, boolean>>({});
  const [savedMap, setSavedMap] = useState<Record<string, boolean>>({});

  async function save(section: string, data: object) {
    setSaving(s => ({ ...s, [section]: true }));
    try {
      await adminBrandingApi.update(hospitalId, data);
      queryClient.invalidateQueries({ queryKey: ['admin-branding', hospitalId] });
      setSavedMap(s => ({ ...s, [section]: true }));
      setTimeout(() => setSavedMap(s => ({ ...s, [section]: false })), 3000);
    } finally { setSaving(s => ({ ...s, [section]: false })); }
  }

  async function uploadLogo() {
    if (!logoPreview || logoPreview === logo) return;
    setSaving(s => ({ ...s, logo: true }));
    setLogoErr('');
    try {
      await adminBrandingApi.uploadLogo(hospitalId, logoPreview);
      setLogo(logoPreview);
      queryClient.invalidateQueries({ queryKey: ['admin-branding', hospitalId] });
      setSavedMap(s => ({ ...s, logo: true }));
      setTimeout(() => setSavedMap(s => ({ ...s, logo: false })), 3000);
    } catch { setLogoErr('Upload failed.'); }
    finally { setSaving(s => ({ ...s, logo: false })); }
  }

  function handleLogoFile(file: File) {
    setLogoErr('');
    if (!['image/png','image/jpeg','image/svg+xml','image/webp'].includes(file.type)) {
      setLogoErr('Only PNG, JPEG, SVG, WebP allowed.'); return;
    }
    if (file.size > 500_000) { setLogoErr('Must be under 500 KB.'); return; }
    const reader = new FileReader();
    reader.onload = e => setLogoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  if (isLoading) return <div className="py-10 text-center text-gray-400 text-sm"><Loader2 size={18} className="animate-spin inline"/></div>;

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Logo */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-2"><Image size={14} className="text-navy"/>
            <span className="font-semibold text-navy text-sm">Hospital Logo</span></div>
          <button onClick={uploadLogo} disabled={saving.logo || !logoPreview || logoPreview === logo}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-orange text-gray-900
                       text-xs font-semibold hover:bg-orange-dark disabled:opacity-50 transition">
            {saving.logo ? <Loader2 size={11} className="animate-spin"/> :
             savedMap.logo ? <CheckCircle2 size={11} className="text-green-700"/> : <Upload size={11}/>}
            {savedMap.logo ? 'Saved' : 'Upload'}
          </button>
        </div>
        <div className="p-5 flex items-center gap-5">
          <div onClick={() => fileRef.current?.click()}
            className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center
                       justify-center cursor-pointer hover:border-orange/50 transition overflow-hidden">
            {logoPreview
              ? <img src={logoPreview} alt="logo" className="w-full h-full object-contain p-1"/>
              : <Upload size={18} className="text-gray-300"/>}
          </div>
          <div className="text-xs text-gray-500 space-y-1">
            <p>PNG, JPEG, SVG or WebP · max 500 KB</p>
            <p>Shown on all hospital prints</p>
            {logoErr && <p className="text-red-400">{logoErr}</p>}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden" onChange={e => e.target.files?.[0] && handleLogoFile(e.target.files[0])}/>
        </div>
      </div>

      <AdminSectionCard icon={Building2} title="Identity & Contact"
        onSave={() => save('identity', identity)}
        saving={saving.identity ?? false} saved={savedMap.identity ?? false}>
        <div className="grid grid-cols-2 gap-3">
          <AdminField label="Tagline" value={identity.branding_tagline}
            onChange={v => setIdentity(s => ({ ...s, branding_tagline: v }))}/>
          <AdminField label="License / Reg. No." value={identity.branding_license_no}
            onChange={v => setIdentity(s => ({ ...s, branding_license_no: v }))}/>
        </div>
        <AdminField label="Address" value={identity.hospital_address} rows={2}
          onChange={v => setIdentity(s => ({ ...s, hospital_address: v }))}/>
        <div className="grid grid-cols-2 gap-3">
          <AdminField label="Phone" value={identity.hospital_phone}
            onChange={v => setIdentity(s => ({ ...s, hospital_phone: v }))}/>
          <AdminField label="Email" value={identity.hospital_email}
            onChange={v => setIdentity(s => ({ ...s, hospital_email: v }))}/>
        </div>
      </AdminSectionCard>

      <AdminSectionCard icon={FileText} title="Prescription Footer"
        onSave={() => save('rx', rx)} saving={saving.rx ?? false} saved={savedMap.rx ?? false}>
        <AdminField label="Additional footer text" value={rx.branding_rx_footer} rows={2}
          onChange={v => setRx({ branding_rx_footer: v })}/>
      </AdminSectionCard>

      <AdminSectionCard icon={Receipt} title="Invoice Footer"
        onSave={() => save('invoice', invoice)} saving={saving.invoice ?? false} saved={savedMap.invoice ?? false}>
        <AdminField label="Payment terms / bank details" value={invoice.branding_invoice_footer} rows={3}
          onChange={v => setInvoice({ branding_invoice_footer: v })}/>
      </AdminSectionCard>

      <AdminSectionCard icon={FlaskConical} title="Lab Report Footer"
        onSave={() => save('lab', lab)} saving={saving.lab ?? false} saved={savedMap.lab ?? false}>
        <AdminField label="Accreditation / notes" value={lab.branding_lab_footer} rows={2}
          onChange={v => setLab({ branding_lab_footer: v })}/>
      </AdminSectionCard>

      <AdminSectionCard icon={Scan} title="Radiology Report Footer"
        onSave={() => save('radiology', radiology)} saving={saving.radiology ?? false} saved={savedMap.radiology ?? false}>
        <AdminField label="Disclaimer / radiologist note" value={radiology.branding_radiology_footer} rows={2}
          onChange={v => setRadiology({ branding_radiology_footer: v })}/>
      </AdminSectionCard>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',  label: 'Overview',  icon: Building2 },
  { id: 'modules',   label: 'Modules',   icon: Layers    },
  { id: 'users',     label: 'Users',     icon: Users     },
  { id: 'branding',  label: 'Branding',  icon: Palette   },
];

export default function HospitalDetail() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState('overview');

  const { data: hospital, isLoading } = useQuery({
    queryKey: ['hospital', id],
    queryFn:  () => adminApi.hospital(id!).then(r => r.data),
    enabled:  !!id,
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center h-64 text-gray-400 text-sm">
        Loading hospital…
      </div>
    );
  }

  if (!hospital) {
    return <div className="p-8 text-clinical-danger">Hospital not found.</div>;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-8 pt-6 pb-0">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-3">
          <Link to="/admin/hospitals" className="hover:text-orange transition-colors">Hospitals</Link>
          <ChevronRight size={12}/>
          <span className="text-navy font-medium">{hospital.hospital_name}</span>
        </div>

        {/* Title + badge */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-navy">{hospital.hospital_name}</h1>
            <p className="text-gray-400 text-xs mt-0.5 font-mono">{hospital.schema_name}</p>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={clsx('inline-flex px-2.5 py-1 rounded-full text-xs font-medium capitalize',
              STATUS_STYLES[hospital.status] ?? 'bg-gray-100 text-gray-400')}>
              {hospital.status?.replace('_', ' ')}
            </span>
            {hospital.plan && (
              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-navy/10 text-navy">
                {PLAN_LABELS[hospital.plan] ?? hospital.plan}
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 -mb-px">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={clsx(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.id
                  ? 'border-orange text-orange'
                  : 'border-transparent text-gray-500 hover:text-navy hover:border-gray-200'
              )}>
              <t.icon size={14}/>
              {t.label}
              {t.id === 'users' && hospital.user_count !== undefined && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-2xs bg-gray-100 text-gray-500">
                  {hospital.user_count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-8">
        {tab === 'overview'  && <OverviewTab hospital={hospital} id={id!}/>}
        {tab === 'modules'   && <ModulesTab id={id!}/>}
        {tab === 'users'     && <UsersTab id={id!}/>}
        {tab === 'branding'  && <BrandingTab hospitalId={id!}/>}
      </div>
    </div>
  );
}
